import { Box, IconButton } from '@mui/material';
import { RotateLeft } from '@mui/icons-material';
import { Icon } from '@/components/ui';
import { closeIcon, chevron } from '@/assets';
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { sitesApi } from '../services';
import type { PhotoData } from '../types';

interface ImageGalleryProps {
  imageId: number;
  photoList: PhotoData[];
  changeSelectedImage: (id: number | null) => void;
  onPhotoRotated?: (oldPhotoId: number, newPhotoData: PhotoData) => void;
  siteId: number;
}

export const ImageGallery = ({
  imageId,
  photoList,
  changeSelectedImage,
  onPhotoRotated,
  siteId,
}: ImageGalleryProps) => {
  const { t } = useTranslation();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';
  const [isRotating, setIsRotating] = useState(false);

  const getImage = () => {
    const index = photoList.findIndex(item => item.id === imageId);
    return index >= 0 ? photoList[index] : null;
  };

  const handleClose = () => {
    changeSelectedImage(null);
  };

  // Add keyboard event handler for ESC key and browser back button
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    const handlePopState = () => {
      // Prevent default browser back behavior and close gallery instead
      handleClose();
    };

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('popstate', handlePopState);

    // Push a dummy state when gallery opens to create a history entry
    window.history.pushState({ galleryOpen: true }, '');

    return () => {
      // Clean up event listeners
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handlePreImage = () => {
    const index = photoList.findIndex(item => item.id === imageId);
    if (index >= 0) {
      const prevIndex = index === 0 ? photoList.length - 1 : index - 1;
      if (photoList[prevIndex]) {
        changeSelectedImage(photoList[prevIndex].id);
      }
    }
  };

  const handleNextImage = () => {
    const index = photoList.findIndex(item => item.id === imageId);
    if (index >= 0) {
      const nextIndex = index === photoList.length - 1 ? 0 : index + 1;
      if (photoList[nextIndex]) {
        changeSelectedImage(photoList[nextIndex].id);
      }
    }
  };

  const handleRotateImage = async () => {
    if (isRotating || !onPhotoRotated) return;

    const currentImage = getImage();
    if (!currentImage || !currentImage.dataUrl) return;

    setIsRotating(true);
    try {
      // Call backend to rotate the image 90 degrees clockwise
      const response = await sitesApi.rotatePhoto(siteId, currentImage.id);

      if (response.isSuccess && response.result?.data) {
        // Create new photo data with the rotated image from backend
        const newPhotoData: PhotoData = {
          id: currentImage.id,
          dataUrl: response.result.data.base64Image.startsWith('data:')
            ? response.result.data.base64Image
            : `data:image/jpeg;base64,${response.result.data.base64Image}`,
        };

        // Notify parent component about the rotation
        onPhotoRotated(currentImage.id, newPhotoData);
      } else {
        console.error('Failed to rotate image:', response.error);
      }
    } catch (error) {
      console.error('Failed to rotate image:', error);
    } finally {
      setIsRotating(false);
    }
  };

  // Early return if no image found
  const currentImage = getImage();
  if (!currentImage) {
    console.log(
      'ImageGallery: No image found for ID',
      imageId,
      'in photoList:',
      photoList.map(p => p.id)
    );
    return null;
  }

  return (
    <Box>
      <Box
        className='image-item'
        sx={{
          height: '100vh',
          background: '#000000cc',
          width: '100vw',
          position: 'fixed',
          top: '0',
          left: '0',
          zIndex: 500,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'relative' }}>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#F5F5F5',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                left: isRTL ? '' : '40px',
                right: isRTL ? '40px' : '',
                top: '40px',
              }}
              onClick={handleClose}
            >
              <Icon src={closeIcon} />
            </Box>

            {/* Rotation button */}
            {onPhotoRotated && (
              <Box
                sx={{
                  position: 'absolute',
                  left: isRTL ? '40px' : '',
                  right: isRTL ? '' : '40px',
                  top: '40px',
                }}
              >
                <IconButton
                  onClick={handleRotateImage}
                  disabled={isRotating}
                  sx={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#F5F5F5',
                    color: isRotating ? '#999' : '#333',
                    '&:hover': {
                      backgroundColor: '#E0E0E0',
                    },
                    '&:disabled': {
                      backgroundColor: '#F5F5F5',
                      color: '#999',
                    },
                  }}
                  title={t('rotate_image')}
                >
                  <RotateLeft fontSize='small' />
                </IconButton>
              </Box>
            )}
            <img
              style={{
                padding: '16px 0px',
                maxWidth: '100%',
                maxHeight: '100%',
              }}
              height='auto'
              width='auto'
              src={currentImage.dataUrl}
              alt=''
            />
          </Box>
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#F5F5F5',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              right: '40px',
              position: 'absolute',
              top: '0px',
              bottom: '0px',
              margin: 'auto',
              transform: isRTL ? 'rotate(90deg)' : 'rotate(-90deg)',
            }}
            onClick={handleNextImage}
          >
            <Icon src={chevron} />
          </Box>
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#F5F5F5',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              left: '40px',
              position: 'absolute',
              top: '0px',
              bottom: '0px',
              margin: 'auto',
              transform: isRTL ? 'rotate(-90deg)' : 'rotate(90deg)',
            }}
            onClick={handlePreImage}
          >
            <Icon src={chevron} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
